import { useEffect, useRef, useState, type ReactElement } from 'react';

import type { NativeReuseContext } from './native-booking-api';

const NEW_VALUE = 'NEW';
const UNSET = '';

type SelectionIntent = typeof UNSET | typeof NEW_VALUE | string;

export function NativeReuseProfiles(props: {
  readonly reuseContext: NativeReuseContext | null;
  readonly disabled: boolean;
  readonly onSelect: (input: {
    readonly selectedContractorProfileId: string | null;
    readonly selectedVehicleProfileId: string | null;
  }) => void;
}): ReactElement | null {
  const ctx = props.reuseContext;
  const [contractorIntent, setContractorIntent] = useState<SelectionIntent>(UNSET);
  const [vehicleIntent, setVehicleIntent] = useState<SelectionIntent>(UNSET);
  const lastContractorChoice = useRef<SelectionIntent>(UNSET);
  const lastVehicleChoice = useRef<SelectionIntent>(UNSET);

  useEffect(() => {
    if (ctx === null) {
      setContractorIntent(UNSET);
      setVehicleIntent(UNSET);
      lastContractorChoice.current = UNSET;
      lastVehicleChoice.current = UNSET;
      return;
    }

    const contractorChoice = ctx.contractor_selection_choice ?? 'UNSET';
    const vehicleChoice = ctx.vehicle_selection_choice ?? 'UNSET';

    if (contractorChoice === 'NEW') {
      setContractorIntent(NEW_VALUE);
      lastContractorChoice.current = NEW_VALUE;
    } else if (ctx.selected_contractor_profile_id !== null) {
      setContractorIntent(ctx.selected_contractor_profile_id);
      lastContractorChoice.current = ctx.selected_contractor_profile_id;
    } else if (lastContractorChoice.current === NEW_VALUE) {
      setContractorIntent(NEW_VALUE);
    } else if (ctx.contractor_options.length === 0) {
      setContractorIntent(NEW_VALUE);
      lastContractorChoice.current = NEW_VALUE;
    } else {
      setContractorIntent(UNSET);
    }

    if (vehicleChoice === 'NEW') {
      setVehicleIntent(NEW_VALUE);
      lastVehicleChoice.current = NEW_VALUE;
    } else if (ctx.selected_vehicle_profile_id !== null) {
      setVehicleIntent(ctx.selected_vehicle_profile_id);
      lastVehicleChoice.current = ctx.selected_vehicle_profile_id;
    } else if (lastVehicleChoice.current === NEW_VALUE) {
      setVehicleIntent(NEW_VALUE);
    } else {
      setVehicleIntent(UNSET);
    }
  }, [ctx]);

  if (ctx === null) {
    return null;
  }
  const hasContractorOptions = ctx.contractor_options.length > 0;
  if (!hasContractorOptions && ctx.resolution_state === 'UNRESOLVED') {
    return null;
  }

  const showVehicle = contractorIntent !== UNSET && contractorIntent !== NEW_VALUE;

  return (
    <section className="native-reuse-profiles reuse-options" aria-label="Профили организации и ТС">
      <h3 className="native-form-doc-title">Организация и транспорт</h3>
      <p className="meta">
        Выберите существующие профили или явно создайте новые. Транспортное средство нужно выбрать
        вручную — автоподбор по госномеру не выполняется. В списке только ТС, связанные с выбранной
        организацией для вашего входа. При точном совпадении госномера/VIN с уже связанным профилем
        система запросит выбор существующего, а не создаст дубликат.
      </p>
      <label className="native-field">
        Организация
        <select
          className="native-input"
          disabled={props.disabled}
          value={contractorIntent}
          data-reuse-contractor-choice={
            contractorIntent === NEW_VALUE
              ? 'NEW'
              : contractorIntent === UNSET
                ? 'UNSET'
                : 'EXISTING'
          }
          onChange={(event) => {
            const raw = event.target.value;
            if (raw === UNSET) {
              return;
            }
            lastContractorChoice.current = raw;
            lastVehicleChoice.current = UNSET;
            setContractorIntent(raw);
            setVehicleIntent(UNSET);
            props.onSelect({
              selectedContractorProfileId: raw === NEW_VALUE ? null : raw,
              selectedVehicleProfileId: null,
            });
          }}
        >
          {hasContractorOptions ? (
            <option value={UNSET} disabled>
              — Выберите организацию —
            </option>
          ) : null}
          {ctx.contractor_options.map((option) => (
            <option key={option.contractor_profile_id} value={option.contractor_profile_id}>
              {option.display_name}
            </option>
          ))}
          <option value={NEW_VALUE}>Новая организация</option>
        </select>
      </label>
      {showVehicle ? (
        <label className="native-field">
          Транспортное средство
          <select
            className="native-input"
            disabled={props.disabled}
            value={vehicleIntent}
            onChange={(event) => {
              const raw = event.target.value;
              if (raw === UNSET) {
                return;
              }
              lastVehicleChoice.current = raw;
              setVehicleIntent(raw);
              props.onSelect({
                selectedContractorProfileId:
                  contractorIntent === NEW_VALUE || contractorIntent === UNSET
                    ? null
                    : contractorIntent,
                selectedVehicleProfileId: raw === NEW_VALUE ? null : raw,
              });
            }}
          >
            <option value={UNSET} disabled>
              — Выберите ТС —
            </option>
            {ctx.vehicle_options.map((option) => (
              <option key={option.vehicle_profile_id} value={option.vehicle_profile_id}>
                {option.display_name}
              </option>
            ))}
            <option value={NEW_VALUE}>Новое ТС</option>
          </select>
          {ctx.vehicle_options.length === 0 ? (
            <span className="meta" data-testid="native-reuse-vehicle-empty-hint">
              Для выбранной организации связанных ТС пока нет. Выберите «Новое ТС» или попросите
              администратора связать существующий профиль с этой организацией.
            </span>
          ) : (
            <span className="meta" data-testid="native-reuse-vehicle-manual-hint">
              Выберите ТС из списка вручную.
            </span>
          )}
        </label>
      ) : null}
    </section>
  );
}
